import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { StyledContainer } from '../../../components/styled/CommonStyles';
import LoadingIndicator from '../../../components/common/LoadingIndicator';
import CustomAlert from '../../../components/common/CustomAlert';
import { useAuth } from '../../../hooks/useAuth';
import FilterSection from './components/FilterSection';
import InvoiceTable from './components/InvoiceTable';
import InvoiceMobileView from './components/InvoiceMobileView';
import TableControls from './components/TableControls';
import StatusUpdateDialog from './components/StatusUpdateDialog';
import InvoicePrintModal from './components/InvoicePrintModal';
import { setDownloadIndicatorRef } from '../../../utils/excelDownload';
import { useRestoredSearchState, useSearchStateCache } from '../../../utils/searchStateCache';

import { useInvoiceManagementState } from './hooks/useInvoiceManagementState';
import { useInvoiceManagementData } from './hooks/useInvoiceManagementData';
import { useInvoiceManagementHandlers } from './hooks/useInvoiceManagementHandlers';
import {
    getBasicColumns,
    getPrintColumns,
    getDefaultInvoicePrintCheckedColumns
} from './constants/invoiceManagementConstants';

const InvoiceManagement = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { user } = useAuth();
    const [invoicePreviewOpen, setInvoicePreviewOpen] = useState(false);
    const [invoicePreviewSelectedIds, setInvoicePreviewSelectedIds] = useState([]);
    const [invoicePreviewStubRows, setInvoicePreviewStubRows] = useState([]);

    const defaultPrintCheckedColumns = useMemo(() => getDefaultInvoicePrintCheckedColumns(t), [t]);

    const handleInvoicePreviewById = useCallback((invoiceId) => {
        const id = Number(invoiceId);
        if (!Number.isFinite(id)) return;
        setInvoicePreviewSelectedIds([id]);
        setInvoicePreviewStubRows([{ invoice_id: id }]);
        setInvoicePreviewOpen(true);
    }, []);

    const cachedState = useRestoredSearchState('invoiceManagement');

    const state = useInvoiceManagementState(t, cachedState);
    const {
        searchParams,
        page,
        rowsPerPage,
        showExtendedView,
        showMyOrderOnly,
        checkedColumns,
        columnPanelOpen,
        selectedItems,
        alertInfo,
        statusUpdateDialogOpen,
        setAlertInfo,
        setStatusUpdateDialogOpen,
        setSelectedItems,
        setSearchParams,
        setPage,
        setInvoiceData,
        setTotalCount,
        setHasSearched,
        setShowMyOrderOnly,
        handleParamChange,
        handleChangePage,
        handleChangeRowsPerPage,
        handleCheckColumn,
        toggleColumnPanel,
        selectAllColumns,
        unselectAllColumns,
        handleSelectItem,
        handleSelectAll,
        handleCloseAlert
    } = state;

    const {
        isLoading,
        invoiceData,
        totalCount,
        fetchInvoiceData,
        fetchInitialData
    } = useInvoiceManagementData(cachedState);

    const { consumeInitialFetchSkip } = useSearchStateCache(
        'invoiceManagement',
        { searchParams, invoiceData, totalCount, page },
        { enabled: invoiceData.length > 0 }
    );

    const handlers = useInvoiceManagementHandlers(
        t,
        selectedItems,
        setAlertInfo,
        setStatusUpdateDialogOpen,
        setSelectedItems,
        fetchInvoiceData,
        setSearchParams,
        setPage,
        setInvoiceData,
        setTotalCount,
        setHasSearched,
        searchParams,
        isLoading
    );

    const {
        handleSearch,
        handleReset,
        handleStatusUpdate,
        handleStatusUpdateConfirm
    } = handlers;

    useEffect(() => {
        const loadInitialData = async () => {
            try {
                await fetchInitialData();
            } catch (error) {
                setAlertInfo({
                    open: true,
                    title: t('error.title'),
                    message: t('error.fetch'),
                    severity: 'error'
                });
            }
        };

        if (consumeInitialFetchSkip()) return;
        loadInitialData();
    }, [t, fetchInitialData, setAlertInfo, consumeInitialFetchSkip]);

    useEffect(() => {
        if (state.hasSearched) {
            fetchInvoiceData(searchParams, setHasSearched);
        }
    }, [state.hasSearched, page, rowsPerPage, fetchInvoiceData, searchParams, setHasSearched]);

    const columnsToUse = useMemo(() => getBasicColumns(t), [t]);
    const printColumnsToUse = useMemo(() => getPrintColumns(t), [t]);

    // 내 오더만 보기: created_by가 로그인 사용자 user_id와 일치하는 행만 표시
    const displayData = useMemo(() => {
        if (!showMyOrderOnly || !user?.user_id) return invoiceData;
        const loginId = (user.user_id || '').toString().toLowerCase();
        return invoiceData.filter(
            (row) => (row.created_by || '').toString().toLowerCase() === loginId
        );
    }, [invoiceData, showMyOrderOnly, user?.user_id]);

    useEffect(() => {
        const handleGlobalKeyDown = (e) => {
            // F3: 검색
            if (e.key === 'F3' && !isLoading) {
                e.preventDefault();
                handleSearch();
            }
            // F5: 데이터 불러오기
            if (e.key === 'F5' && !isLoading) {
                e.preventDefault();
                handleSearch();
            }
            // F10: 초기화
            if (e.key === 'F10' && !isLoading) {
                e.preventDefault();
                handleReset();
            }
            // Ctrl+F: 검색
            if (e.key === 'f' && e.ctrlKey && !isLoading) {
                e.preventDefault();
                handleSearch();
            }
            // Esc: 취소/Close
            if (e.key === 'Escape') {
                e.preventDefault();
                setAlertInfo(prev => ({ ...prev, open: false }));
                setStatusUpdateDialogOpen(false);
                setInvoicePreviewOpen(false);
            }
        };

        window.addEventListener('keydown', handleGlobalKeyDown);
        return () => window.removeEventListener('keydown', handleGlobalKeyDown);
    }, [handleSearch, handleReset, isLoading]);

    return (
        <StyledContainer>
            <LoadingIndicator isLoading={isLoading} message={t('common.loading')} />

            <CustomAlert
                open={alertInfo.open}
                title={alertInfo.title}
                message={alertInfo.message}
                severity={alertInfo.severity}
                onConfirm={alertInfo.onConfirm || handleCloseAlert}
                onClose={alertInfo.onClose || handleCloseAlert}
            />

            <FilterSection
                searchParams={searchParams}
                handleParamChange={handleParamChange}
                handleSearch={handleSearch}
                handleReset={handleReset}
                isLoading={isLoading}
            />

            <Box sx={{ width: '100%' }}>
                <TableControls
                    t={t}
                    showMyOrderOnly={showMyOrderOnly}
                    setShowMyOrderOnly={(checked) => {
                        setShowMyOrderOnly(checked);
                        setPage(0);
                    }}
                    toggleColumnPanel={toggleColumnPanel}
                    columnPanelOpen={columnPanelOpen}
                    columnsToUse={printColumnsToUse}
                    checkedColumns={checkedColumns}
                    handleCheckColumn={handleCheckColumn}
                    selectAllColumns={selectAllColumns}
                    unselectAllColumns={unselectAllColumns}
                    selectedItems={selectedItems}
                    onStatusUpdate={handleStatusUpdate}
                />

                {isMobile ? (
                    <InvoiceMobileView
                        invoiceData={displayData}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        selectedItems={selectedItems}
                        onSelectItem={handleSelectItem}
                        onInvoicePreviewById={handleInvoicePreviewById}
                        t={t}
                    />
                ) : (
                    <InvoiceTable
                        invoiceData={displayData}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        totalCount={displayData.length}
                        isLoading={isLoading}
                        columnsToUse={columnsToUse}
                        checkedColumns={checkedColumns}
                        selectedItems={selectedItems}
                        onSelectItem={handleSelectItem}
                        onSelectAll={handleSelectAll}
                        handleChangePage={handleChangePage}
                        handleChangeRowsPerPage={handleChangeRowsPerPage}
                        onInvoicePreviewById={handleInvoicePreviewById}
                        t={t}
                    />
                )}
            </Box>

            <StatusUpdateDialog
                open={statusUpdateDialogOpen}
                onClose={() => setStatusUpdateDialogOpen(false)}
                onConfirm={handleStatusUpdateConfirm}
                selectedItems={selectedItems}
                invoiceData={displayData}
                t={t}
            />

            <InvoicePrintModal
                open={invoicePreviewOpen}
                onClose={() => setInvoicePreviewOpen(false)}
                selectedInvoices={invoicePreviewSelectedIds}
                invoiceData={invoicePreviewStubRows}
                initialColumns={defaultPrintCheckedColumns}
            />
        </StyledContainer>
    );
};

export default InvoiceManagement; 